import React, { useEffect, useRef, useState } from "react";
import { Upload, X, CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Progress } from "./ui/progress";
import { useToast } from "../hooks/use-toast";
import api from "../lib/api";

const EMPTY = { first: "", last: "", email: "", confirmEmail: "", phone: "", location: "", message: "" };
const MAX_FILES = 8;
const MAX_FILE_MB = 12;
const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif", "image/gif"];
const newKey = () =>
  `idem_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

export default function EstimateForm({ variant = "light", title }) {
  const [values, setValues] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);
  const idemRef = useRef(newKey());
  const loadedAt = useRef(Date.now());
  const { toast } = useToast();

  const onHero = variant === "hero";

  useEffect(() => {
    loadedAt.current = Date.now();
  }, []);

  const set = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    setErrors((p) => ({ ...p, [k]: undefined, _form: undefined }));
  };

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    const rejected = [];
    const ok = [];
    incoming.forEach((f) => {
      if (!ALLOWED.includes(f.type)) {
        rejected.push(`${f.name} is not a supported image type`);
      } else if (f.size > MAX_FILE_MB * 1024 * 1024) {
        rejected.push(`${f.name} is larger than ${MAX_FILE_MB} MB`);
      } else {
        ok.push(f);
      }
    });
    if (rejected.length) {
      setErrors((p) => ({ ...p, files: rejected.join(". ") }));
    } else {
      setErrors((p) => ({ ...p, files: undefined, _form: undefined }));
    }
    if (ok.length) setFiles((p) => [...p, ...ok].slice(0, MAX_FILES));
  };

  const validate = () => {
    const e = {};
    const email = values.email.trim();
    const confirm = values.confirmEmail.trim();
    if (!values.first.trim()) e.first = "First name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = "Enter a valid email address.";
    if (!confirm) e.confirmEmail = "Please confirm your email address.";
    else if (confirm.toLowerCase() !== email.toLowerCase()) e.confirmEmail = "Email addresses must match.";
    if (values.phone.replace(/\D/g, "").length < 10) e.phone = "Enter a valid phone number.";
    if (!values.location.trim()) e.location = "Please tell us where the job is located.";
    if (!files.length) e.files = "Please attach at least one photo of your project.";
    if (!values.message.trim()) e.message = "A message is required.";
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    const e = validate();
    if (Object.keys(e).length) {
      setErrors({
        ...e,
        _form: "There was a problem with your submission. Errors have been highlighted below.",
      });
      return;
    }
    setSubmitting(true);
    setProgress(0);
    const fd = new FormData();
    fd.append("first_name", values.first.trim());
    fd.append("last_name", values.last.trim());
    fd.append("email", values.email.trim());
    fd.append("confirm_email", values.confirmEmail.trim());
    fd.append("phone", values.phone.trim());
    fd.append("job_location", values.location.trim());
    fd.append("message", values.message.trim());
    fd.append("idempotency_key", idemRef.current);
    fd.append("website", ""); // honeypot, must remain empty
    fd.append("form_loaded_at", String(loadedAt.current));
    files.forEach((f) => fd.append("photos", f, f.name));

    try {
      const { data } = await api.post("/estimates", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) =>
          setProgress(p.total ? Math.round((p.loaded / p.total) * 100) : 0),
      });
      setResult(data);
      setValues(EMPTY);
      setFiles([]);
      setErrors({});
      toast({
        title: data.duplicate ? "Already received" : "Estimate request sent",
        description: data.duplicate
          ? `We already have this request (ref ${data.reference}). Nothing was sent twice.`
          : `Reference ${data.reference}. Ernest usually replies within one business day.`,
      });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 422 && detail?.errors) {
        const map = {
          first_name: "first", last_name: "last", email: "email",
          confirm_email: "confirmEmail", phone: "phone",
          job_location: "location", message: "message", photos: "files",
        };
        const mapped = {};
        Object.entries(detail.errors).forEach(([k, v]) => {
          mapped[map[k] || k] = v;
        });
        setErrors({
          ...mapped,
          _form: "There was a problem with your submission. Errors have been highlighted below.",
        });
      } else if (status === 429) {
        setErrors({ _form: typeof detail === "string" ? detail : "Too many submissions. Please try again later." });
      } else {
        setErrors({
          _form:
            (typeof detail === "string" && detail) ||
            "We could not send your request. Please call (203) 578-5260 or try again.",
        });
      }
    } finally {
      setSubmitting(false);
      setProgress(0);
    }
  };

  const labelCls = onHero
    ? "text-white text-[13px] font-bold uppercase tracking-wide"
    : "text-[#242424] text-[13px] font-bold uppercase tracking-wide";
  const inputCls = onHero
    ? "mt-1.5 h-11 border-white/40 bg-white/95 text-[#242424] placeholder:text-neutral-400 focus-visible:ring-white"
    : "mt-1.5 h-11 border-neutral-300 bg-white text-[#242424] placeholder:text-neutral-400 focus-visible:ring-[#244aa2]";
  const hintCls = onHero ? "text-white/70" : "text-neutral-500";
  const errCls = `text-[12px] font-semibold ${onHero ? "text-[#ffc9c9]" : "text-[#a10000]"}`;

  if (result) {
    return (
        <div className={`flex flex-col items-center gap-4 p-10 text-center ${onHero ? "text-white" : "text-[#242424]"}`} role="status" data-testid="estimate-success">
        <CheckCircle2 className={`h-14 w-14 ${onHero ? "text-white" : "text-[#244aa2]"}`} />
        <h3 className="font-head text-2xl font-extrabold">Thank you — we&rsquo;ve got it.</h3>
        <p className={onHero ? "max-w-lg text-white/90" : "max-w-lg text-[#575757]"}>
          Your request has been saved with reference{" "}
          <strong className="whitespace-nowrap" data-testid="estimate-reference">{result.reference}</strong> and{" "}
          {result.photo_count} photo{result.photo_count === 1 ? "" : "s"} attached. Ernest usually
          responds within one business day. For the fastest quote you can also text your photos to
          (203) 578-5260.
        </p>
        <button
          type="button"
          onClick={() => {
            idemRef.current = newKey();
            loadedAt.current = Date.now();
            setResult(null);
          }}
          className={`mt-2 px-6 py-3 text-[12px] font-bold uppercase tracking-wide transition-colors duration-200 ${
            onHero ? "bg-white text-[#244aa2] hover:bg-[#e8eefb]" : "bg-[#244aa2] text-white hover:bg-[#0c2a66]"
          }`}
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="w-full">
      {title && (
        <h3 className={`font-head mb-5 text-center text-2xl font-extrabold ${onHero ? "text-white" : "text-[#244aa2]"}`}>
          {title}
        </h3>
      )}

      <div className="mb-6">
        <p className={`text-[12px] font-bold uppercase tracking-widest ${onHero ? "text-white/80" : "text-neutral-500"}`}>
          Step 1 of 1
        </p>
        <div className="mt-2 flex items-center gap-3">
          <Progress value={submitting ? progress : 100} className={onHero ? "h-2 bg-white/25" : "h-2 bg-neutral-200"} />
          <span className={`text-[12px] font-bold ${onHero ? "text-white" : "text-[#244aa2]"}`}>
            {submitting ? `${progress}%` : "100%"}
          </span>
        </div>
      </div>

      {errors._form && (
        <div
          role="alert"
          data-testid="estimate-form-error"
          className={`mb-5 border-l-4 px-4 py-3 text-sm font-semibold ${
            onHero ? "border-[#ffc9c9] bg-white/15 text-white" : "border-[#a10000] bg-[#a10000]/10 text-[#a10000]"
          }`}
        >
          {errors._form}
        </div>
      )}

      {/* honeypot: hidden from humans, bots fill it in */}
      <div className="absolute left-[-9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
        <label htmlFor="website-url">Website</label>
        <input id="website-url" name="website" tabIndex={-1} autoComplete="off" />
      </div>

      <div className="space-y-5">
        <div>
          <Label className={labelCls} htmlFor="ef-first">Name <span className="text-[#a10000]">*</span></Label>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Input id="ef-first" data-testid="estimate-first-name" aria-required="true" className={inputCls} value={values.first} onChange={set("first")} placeholder="First" autoComplete="given-name" />
              <span className={`mt-1 block text-[11px] ${hintCls}`}>First</span>
              {errors.first && <p className={errCls} data-testid="estimate-error-first-name">{errors.first}</p>}
            </div>
            <div>
              <Input id="ef-last" data-testid="estimate-last-name" aria-required="true" className={inputCls} value={values.last} onChange={set("last")} placeholder="Last" autoComplete="family-name" aria-label="Last name" />
              <span className={`mt-1 block text-[11px] ${hintCls}`}>Last</span>
              {errors.last && <p className={errCls} data-testid="estimate-error-last-name">{errors.last}</p>}
            </div>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className={labelCls} htmlFor="ef-email">Email <span className="text-[#a10000]">*</span></Label>
            <Input id="ef-email" data-testid="estimate-email" aria-required="true" className={inputCls} type="email" value={values.email} onChange={set("email")} autoComplete="email" />
            {errors.email && <p className={`mt-1 ${errCls}`} data-testid="estimate-error-email">{errors.email}</p>}
          </div>
          <div>
            <Label className={labelCls} htmlFor="ef-email2">Confirm Email <span className="text-[#a10000]">*</span></Label>
            <Input id="ef-email2" data-testid="estimate-confirm-email" aria-required="true" className={inputCls} type="email" value={values.confirmEmail} onChange={set("confirmEmail")} />
            {errors.confirmEmail && <p className={`mt-1 ${errCls}`} data-testid="estimate-error-confirm-email">{errors.confirmEmail}</p>}
          </div>
        </div>

        <div>
          <Label className={labelCls} htmlFor="ef-phone">Phone <span className="text-[#a10000]">*</span></Label>
          <Input id="ef-phone" data-testid="estimate-phone" aria-required="true" className={inputCls} type="tel" value={values.phone} onChange={set("phone")} autoComplete="tel" />
          {errors.phone && <p className={`mt-1 ${errCls}`} data-testid="estimate-error-phone">{errors.phone}</p>}
        </div>

        <div>
          <Label className={labelCls} htmlFor="ef-loc">Job Location <span className="text-[#a10000]">*</span></Label>
          <Textarea id="ef-loc" data-testid="estimate-job-location" aria-required="true" className={`${inputCls} h-24 min-h-[96px] py-2.5`} maxLength={500} value={values.location} onChange={set("location")} placeholder="Street address, town / city in Connecticut where the job is located" />
          <span className={`mt-1 block text-[11px] ${hintCls}`}>{values.location.length} of 500 max characters</span>
          {errors.location && <p className={errCls} data-testid="estimate-error-location">{errors.location}</p>}
        </div>

        <div>
          <Label className={labelCls}>Upload a Photo of Your Project <span className="text-[#a10000]">*</span></Label>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
            className={`mt-1.5 flex flex-col items-center justify-center gap-2 border-2 border-dashed px-6 py-8 text-center transition-colors duration-200 ${
              dragOver
                ? onHero ? "border-white bg-white/20" : "border-[#244aa2] bg-[#244aa2]/5"
                : onHero ? "border-white/50 bg-white/10" : "border-neutral-300 bg-neutral-50"
            }`}
          >
            <Upload className={`h-7 w-7 ${onHero ? "text-white" : "text-[#244aa2]"}`} />
            <p className={`text-sm ${onHero ? "text-white/90" : "text-[#575757]"}`}>Drop files here or</p>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className={`px-5 py-2 text-[12px] font-bold uppercase tracking-wide transition-colors duration-200 ${
                onHero ? "bg-white text-[#244aa2] hover:bg-[#e8eefb]" : "bg-[#244aa2] text-white hover:bg-[#0c2a66]"
              }`}
            >
              Select files
            </button>
            <span className={`text-[11px] ${hintCls}`}>
              JPG, PNG, WEBP, HEIC or GIF &middot; up to {MAX_FILES} photos &middot; max {MAX_FILE_MB} MB each
            </span>
            <input ref={fileRef} data-testid="estimate-photos-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} />
          </div>
          {!!files.length && (
            <ul className="mt-3 space-y-2">
              {files.map((f, i) => (
                <li key={f.name + i} className={`flex items-center justify-between gap-3 px-3 py-2 text-sm ${onHero ? "bg-white/15 text-white" : "bg-neutral-100 text-[#242424]"}`}>
                  <span className="truncate">{f.name} <span className="opacity-60">({(f.size / 1024 / 1024).toFixed(1)} MB)</span></span>
                  <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles((p) => p.filter((_, x) => x !== i))}>
                    <X className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {errors.files && <p className={`mt-1 ${errCls}`} data-testid="estimate-error-photos">{errors.files}</p>}
        </div>

        <div>
          <Label className={labelCls} htmlFor="ef-msg">Message <span className="text-[#a10000]">*</span></Label>
          <Textarea id="ef-msg" data-testid="estimate-message" aria-required="true" className={`${inputCls} h-32 min-h-[128px] py-2.5`} value={values.message} onChange={set("message")} placeholder="Tell us about your tub, tile or countertop project. Counter photos should include measurements." />
          {errors.message && <p className={`mt-1 ${errCls}`} data-testid="estimate-error-message">{errors.message}</p>}
        </div>

        <button
          type="submit"
          data-testid="estimate-submit-button"
          disabled={submitting}
          className={`flex w-full items-center justify-center gap-2 px-8 py-4 text-[13px] font-bold uppercase tracking-[0.1em] transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto ${
            onHero ? "bg-white text-[#244aa2] hover:bg-[#e8eefb]" : "bg-[#244aa2] text-white hover:bg-[#0c2a66]"
          }`}
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {submitting ? "Sending…" : "Submit"}
        </button>
      </div>
    </form>
  );
}
